import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  MouseEvent,
  KeyboardEvent,
} from "react";
import { useLocation } from "react-router-dom";
import { Tooltip, Spin } from "antd";
import Settings from "@assets/img/settings";
import InfoIcon from "@assets/icons/i.svg";
import { SettingsModal } from "../ExecutiveSummary/SettingsBigModal";
import { useWidgets } from "@hooks/useWidgets";
import { widgetService } from "@services/widget.service";
import { useProductStore } from "src/store/overviewProduct.state";
import { WidgetMetricKey } from "@services/math/math.service";
import {
  defaultWidgetsProducts,
  useDashboardStore,
} from "@pages/Dashboard/store/dashboard.state";
import { WIDGET_METRICS_CONFIG } from "@services/math/math.const";
import { ROUTES } from "src/router/routes";
import styles from "./styles.module.scss";
import { ExpandableTable } from "@components/ExpandebleTable";

interface DataBlockProps {
  showOnly?: "COO" | "Market" | "CFO" | "Ad";
}

const METRIC_COL_WIDTH = 140;
const SETTINGS_COL_WIDTH = 50;
const MAX_TABLE_WIDTH = 1350;

const DataBlock: React.FC<DataBlockProps> = ({ showOnly }) => {
  const { selectedProduct } = useProductStore();
  const location = useLocation();
  const isCRO = location.pathname === `/${ROUTES.CRO}`;
  const { dateRange } = useDashboardStore((state) => ({
    dateRange: state.dateRange,
  }));
  const [choosedWidgets, setChoosedWidgets] = useState<WidgetMetricKey[]>(
    defaultWidgetsProducts,
  );
  const selectedMetrics = useMemo(
    () =>
      choosedWidgets.map((key) => ({
        key,
        name: WIDGET_METRICS_CONFIG[key].label,
        info: WIDGET_METRICS_CONFIG[key].info,
      })),
    [choosedWidgets],
  );

  const [metricsData, setMetricsData] = useState<Record<string, string>>({});
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [showHistorical, setShowHistorical] = useState(false);
  const allowedGroups = showOnly ? [showOnly] : undefined;

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768,
  );
  const [modalPos, setModalPos] = useState({ x: 0, y: 0, w: 0 });
  const ongoing = useRef<string | null>(null);
  const { handleApply } = useWidgets();

  const tableWidth =
    selectedMetrics.length * METRIC_COL_WIDTH + SETTINGS_COL_WIDTH;
  const shouldScroll = tableWidth > MAX_TABLE_WIDTH;

  const openConfigModal = (e: MouseEvent<HTMLButtonElement>) => {
    const { right, bottom, width } = e.currentTarget.getBoundingClientRect();
    setModalPos({ x: right, y: bottom, w: width });
    setIsConfigModalOpen(true);
  };
  const closeConfigModal = useCallback(() => {
    setIsConfigModalOpen(false);
  }, []);
  const applySettings = useCallback(() => {
    handleApply(choosedWidgets);
    setIsConfigModalOpen(false);
  }, [choosedWidgets, handleApply]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    async function fetchMetrics() {
      if (!selectedProduct) return;
      const asin =
        typeof selectedProduct === "string"
          ? selectedProduct
          : selectedProduct.asin;
      const dateStart = dateRange.startDate.toISOString().split("T")[0];
      const dateEnd = dateRange.endDate.toISOString().split("T")[0];

      const cacheKey = `${asin}-${choosedWidgets.join(",")}`;
      if (ongoing.current === cacheKey) return;
      ongoing.current = cacheKey;
      setIsLoadingMetrics(true);

      try {
        const metrics = await widgetService.getWidgetsMetrics({
          dateStart,
          dateEnd,
          asin,
        });
        const map: Record<string, string> = {};
        choosedWidgets.forEach((key) => {
          const raw = metrics[key] ?? 0;
          map[key] = widgetService.formatValue(key, raw);
        });
        setMetricsData(map);
      } finally {
        ongoing.current = null;
        setIsLoadingMetrics(false);
      }
    }
    fetchMetrics();
  }, [selectedProduct, choosedWidgets]);

  const columns = useMemo(() => {
    const cols = selectedMetrics.map((metric) => ({
      title: (
        <div className={styles.metricHeader}>
          {metric.name}
          {(metric.key === "tacos" || metric.key === "roi") && (
            <Tooltip title={metric.info}>
              <img src={InfoIcon} alt="info" className={styles.infoIcon} />
            </Tooltip>
          )}
        </div>
      ),
      dataIndex: metric.key,
      key: metric.key,
      width: METRIC_COL_WIDTH,
      render: (_: any, record: any) =>
        isLoadingMetrics ? (
          <Spin size="small" />
        ) : (
          (record[metric.key] ?? "N/A")
        ),
    }));

    cols.push({
      title: (
        <button
          type="button"
          onClick={openConfigModal}
          className={styles.settingsBtn}
        >
          <Settings />
        </button>
      ),
      key: "settings",
      width: SETTINGS_COL_WIDTH,
      fixed: "right",
      className: styles.settingsCell,
      render: () => null,
    });

    return cols;
  }, [selectedMetrics, isLoadingMetrics, openConfigModal]);

  const dataSource = useMemo(
    () => [{ key: "metrics", ...metricsData }],
    [metricsData],
  );

  return (
    <div className={styles.dataBlock}>
      {!isMobile && (
        <ExpandableTable
          rowKey="key"
          columns={columns}
          data={dataSource}
          isLoading={false}
          hideRowSelection
          withCustomScroll
          scroll={shouldScroll ? { x: tableWidth } : undefined}
          headerClassName
        />
      )}

      {isConfigModalOpen && (
        <dialog
          open
          className={styles.overlay}
          style={{
            position: "absolute",
            top: modalPos.y,
            left: modalPos.x - modalPos.w,
          }}
          onClick={closeConfigModal}
          onKeyDown={(e: KeyboardEvent<HTMLDialogElement>) => {
            if (e.key === "Escape") {
              closeConfigModal();
            }
          }}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <SettingsModal
              open={isConfigModalOpen}
              onClose={closeConfigModal}
              onApply={applySettings}
              choosedWidgets={choosedWidgets}
              setChoosedWidgets={setChoosedWidgets}
              maxWidgets={Infinity}
              allowedGroups={allowedGroups}
              isCRO={isCRO}
              showHistorical={showHistorical}
              setShowHistorical={setShowHistorical}
            />
          </div>
        </dialog>
      )}
    </div>
  );
};

export default DataBlock;
